/**
 * timesheets — the weekly record, its exceptions, and the approval lifecycle.
 *
 * A complete API has existed since Task 12 with no client and no Admin screen,
 * so "which timesheets need approval?" was unanswerable in the product. This is
 * that contract.
 *
 * Reads need `timesheet:read`; approve/return need `timesheet:approve`. The
 * server additionally enforces two rules this client cannot and should not
 * duplicate: a caller without `timesheet:manage` is narrowed to their OWN
 * worker, and nobody may approve the timesheet of the worker linked to their own
 * account.
 *
 * IMPORTANT: hours here are RECORDED time, not pay. The domain is deliberate
 * about that distinction and so is every screen that renders these numbers —
 * overtime, differentials and leave accrual are payroll's job, and Phibian has
 * no payroll domain today.
 */
import { ApiError } from './errors.js';
import { get, post } from './transport.js';
// --- reads ------------------------------------------------------------------
/** `GET /timesheets` — periods, optionally narrowed by worker or status. */
export async function periods(query = {}, options) {
    const body = await get('/timesheets', {
        ...options,
        query: { ...query },
    });
    return body.periods;
}
/**
 * `GET /timesheets/workers/:workerId/week` — the week, its total, and what is
 * wrong with it.
 *
 * `weekOf` is any instant inside the target week; the server anchors it to that
 * week's start in the ORGANIZATION's calendar — `organization.timezone` plus
 * `organization.week_starts_on`, which is NOT always Monday — so a caller never
 * has to compute week boundaries itself and must not try.
 */
export async function workerWeek(workerId, weekOf, options) {
    const body = await get(`/timesheets/workers/${workerId}/week`, {
        ...options,
        query: { weekOf },
    });
    return body.week;
}
/** `GET /timesheets/:id`. */
export async function period(id, options) {
    const body = await get(`/timesheets/${id}`, options);
    return body.period;
}
/**
 * `GET /timesheets/:id/snapshot` — what was approved, as it stood at approval.
 *
 * This is the evidence half of the domain: a period can be reopened by a later
 * correction, and the snapshot is what the approver actually signed off.
 */
export async function snapshot(id, options) {
    const body = await get(`/timesheets/${id}/snapshot`, options);
    return body.snapshot ?? null;
}
// --- lifecycle --------------------------------------------------------------
/** `POST /timesheets/submit` — send a week for approval. */
export async function submit(input, options) {
    const body = await post('/timesheets/submit', input, options);
    return body.period;
}
/**
 * Read an approval failure as the open-session conflict, or `null` when it is
 * something else.
 *
 * THE POINT IS THAT A SCREEN NEVER PARSES `err.body` ITSELF. The server's
 * `message` for this 409 names the wire parameter (`acknowledgeOpenSessions`)
 * and lists raw uuids; showing it verbatim to an approver — which is what
 * happened until this existed — tells a person to go and set a field they have
 * never heard of, in a request they cannot make.
 *
 * `null` for a non-409, for a 409 with a different `code`, and for a body whose
 * shape does not carry the ids. A caller that got `null` still has a real
 * failure to render; it is just not THIS one.
 */
export function openSessionConflict(err) {
    if (!(err instanceof ApiError) || err.status !== 409)
        return null;
    const body = err.body;
    if (typeof body !== 'object' || body === null)
        return null;
    const bag = body;
    if (bag['code'] !== 'open_session')
        return null;
    const open = bag['openSessionIds'];
    if (!Array.isArray(open) || !open.every((id) => typeof id === 'string'))
        return null;
    const unacked = bag['unacknowledgedSessionIds'];
    return {
        openSessionIds: open,
        // An older server sends only `openSessionIds`. Falling back to the full
        // open set is the CONSERVATIVE reading — it claims nothing was
        // acknowledged, which is true on a first attempt and overstates rather
        // than understates on a retry. Defaulting to `[]` would render "nothing is
        // unacknowledged" over a refusal that says otherwise.
        unacknowledgedSessionIds: Array.isArray(unacked) && unacked.every((id) => typeof id === 'string')
            ? unacked
            : open,
    };
}
/**
 * `POST /timesheets/:id/approve`.
 *
 * A week containing an open (never clocked-out) session is REFUSED with a 409
 * carrying `code: 'open_session'` — read it with {@link openSessionConflict} —
 * because an open session contributes 0 minutes and approval freezes an
 * immutable snapshot at that understated total. Re-approve with
 * `acknowledgeOpenSessions` naming those sessions to accept it on the record;
 * the acknowledgement is written into the approval's audit event.
 */
export async function approve(id, input = {}, options) {
    return post(`/timesheets/${id}/approve`, input.acknowledgeOpenSessions
        ? { acknowledgeOpenSessions: [...input.acknowledgeOpenSessions] }
        : {}, options);
}
/**
 * `POST /timesheets/:id/return` — send it back, with a reason.
 *
 * The reason is required by the server, not merely by the form: a week returned
 * without one leaves the worker guessing what to fix.
 */
export async function returnForCorrection(id, reason, options) {
    const body = await post(`/timesheets/${id}/return`, { reason }, options);
    return body.period;
}
//# sourceMappingURL=timesheets.js.map